// Retour de Stripe Checkout et action payée à reprendre — PUR (aucun React, aucun stockage).
import { t } from './translations';
//
// Parcours visé : l'utilisateur lance un acte payant (ex. envoyer un test TrueFit), le serveur
// répond `payment_required`, on ouvre le paiement ; au retour, le paiement est CONFIRMÉ côté
// serveur puis l'acte repart tout seul. Avant : retour sur `/` → redirection vers /menupb2b qui
// perdait `?checkout=…`, aucun écran ne confirmait rien et l'acte payé n'était jamais lancé.

export type CheckoutReturnState = 'success' | 'paid' | 'settled' | 'cancel';

export interface CheckoutReturn {
  state: CheckoutReturnState;
  sessionId: string | null;
  kind: string | null;
}

const STATES: CheckoutReturnState[] = ['success', 'paid', 'settled', 'cancel'];
const CHECKOUT_PARAMS = ['checkout', 'session_id', 'kind'];

export function parseCheckoutReturn(search: string): CheckoutReturn | null {
  const p = new URLSearchParams(search);
  const state = p.get('checkout') as CheckoutReturnState | null;
  if (!state || !STATES.includes(state)) return null;
  const sid = p.get('session_id');
  return {
    state,
    sessionId: sid && /^cs_(test|live)_[A-Za-z0-9]+$/.test(sid) ? sid : null,
    kind: p.get('kind'),
  };
}

/** La query sans les paramètres de retour Stripe (`''` ou `?…`). */
export function stripCheckoutParams(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of CHECKOUT_PARAMS) p.delete(k);
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ── Action payée en attente ─────────────────────────────────────────────────────────────
export const PENDING_PAID_ACTION_KEY = 'tc_pending_paid_action';
export const PENDING_PAID_ACTION_TTL_MS = 2 * 60 * 60 * 1000;
export const BILLING_CONFIRMED_EVENT = 'tc:billing-confirmed';

export interface PendingPaidAction {
  kind: 'truefit_test';
  candidateId: string;
  createdAt: number;
  /** Posé par le retour Stripe une fois le paiement confirmé par le serveur. */
  confirmed?: boolean;
}

export function parsePendingPaidAction(raw: string | null, now: number): PendingPaidAction | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<PendingPaidAction>;
    if (o?.kind !== 'truefit_test' || typeof o.candidateId !== 'string' || !o.candidateId) return null;
    if (typeof o.createdAt !== 'number' || now - o.createdAt > PENDING_PAID_ACTION_TTL_MS || o.createdAt > now + 60_000) return null;
    return { kind: o.kind, candidateId: o.candidateId, createdAt: o.createdAt, confirmed: o.confirmed === true };
  } catch {
    return null;
  }
}

const ERROR_KEYS: Record<string, string> = {
  'not-authorized-for-billing': 'billing.error.notAuthorized',
  'plan-change-failed': 'billing.error.planChangeFailed',
  feature_not_in_plan: 'billing.error.featureNotInPlan',
  'no-subscription': 'billing.error.noSubscription',
  'not-configured': 'billing.error.notConfigured',
  'price-not-found': 'billing.error.priceNotFound',
};

/** Version traduite (clés `billing.error.*`) de billingErrorMessage. */
function billingErrorMessageLocalized(code: string | null | undefined, lang: string, currentCurrency?: unknown): string {
  if (code === 'currency-mismatch') {
    const cur = typeof currentCurrency === 'string' && currentCurrency ? currentCurrency.toUpperCase() : t(lang, 'billing.error.otherCurrency');
    return t(lang, 'billing.error.currencyMismatchFmt').replace('{currency}', cur);
  }
  if (code === 'plan-change-failed') {
    return t(lang, 'billing.error.planChangeFailed').replace('{portal}', t(lang, 'billing.cta.portal'));
  }
  return t(lang, (code && ERROR_KEYS[code]) || 'billing.error.generic');
}

/**
 * Message utilisateur pour un code d'erreur des fonctions de facturation.
 * Avec `extra.lang` : message dans la langue de l'utilisateur ; sans : français (historique).
 */
export function billingErrorMessage(code: string | null | undefined, extra?: { currentCurrency?: unknown; lang?: string }): string {
  if (extra?.lang) return billingErrorMessageLocalized(code, extra.lang, extra.currentCurrency);
  switch (code) {
    case 'not-authorized-for-billing':
      return "Seul le responsable du compte peut modifier l'abonnement ou régler un paiement.";
    case 'currency-mismatch': {
      const cur = typeof extra?.currentCurrency === 'string' ? extra.currentCurrency.toUpperCase() : '';
      return `Votre abonnement est facturé en ${cur || 'une autre devise'} : choisissez une zone de facturation dans cette devise. Pour changer de devise, contactez-nous.`;
    }
    case 'plan-change-failed':
      return "Stripe n'a pas pu appliquer le changement. Vérifiez votre moyen de paiement dans « Gérer mon abonnement », puis réessayez.";
    case 'feature_not_in_plan':
      return "Cette option n'est pas disponible sur votre forfait : passez à un forfait supérieur.";
    case 'no-subscription':
      return "Aucun abonnement Stripe n'est rattaché à ce compte.";
    case 'not-configured':
      return 'Le paiement en ligne est momentanément indisponible.';
    case 'price-not-found':
      return "Ce forfait n'est pas encore disponible au paiement en ligne.";
    default:
      return 'Paiement indisponible pour le moment. Réessayez dans un instant.';
  }
}
